import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AppBar,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Slider,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ToggleButton, ToggleButtonGroup, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet'; // Assuming secondary yellow/accent

import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadowUrl from 'leaflet/dist/images/marker-shadow.png';
import {
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Clear as ClearIcon,
  ContentCopy as ContentCopyIcon,
  FilterAlt as FilterIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
  List as ListIcon,
  Menu as MenuIcon,
  MoreHoriz as MoreIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Share as ShareIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Work as WorkIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthService';
import useUserType from '../hooks/useUserType';
import JobService from '../services/JobService';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import { jobsCache } from '../utils/cacheUtils';
import { formatJobTimestamp } from '../utils/dateUtils';
import { usePostHog } from '@posthog/react';

// Custom icons
const getCustomIcon = (color) => {
  const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${color}" width="32px" height="32px">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>`;
  return L.divIcon({
    className: '',
    html: svgIcon,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};
const JobIcon = getCustomIcon('#0A331C'); // Assuming primary dark green
const UserIcon = getCustomIcon('#FFC107');

let DefaultIcon = L.icon({
  iconUrl,
  shadowUrl: iconShadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});
L.Marker.prototype.options.icon = DefaultIcon;

const LOCAL_STORAGE_KEYS = {
  savedJobs: 'labourando_saved_keys',
  savedLanguage: 'labourando_job_board_language',
};

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'ptBr', label: 'Português' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
];

const DEFAULT_FILTERS = {
  query: '',
  radiusKm: 'all',
  category: 'all',
  role: 'all',
  state: 'all',
  subregion: 'all',
  minRate: 25,
  whiteCard: false,
  ppe: false,
  tools: false,
  sortBy: 'createdAt',
};

const STATE_OPTIONS = ['all', 'NSW', 'VIC', 'QLD', 'WA'];
const CATEGORY_OPTIONS = ['all', 'Construction', 'Traffic Control', 'Warehousing', 'Civil Works', 'Unknown'];
const RADIUS_OPTIONS = [
  { value: 'all', label: 'Nationwide' },
  { value: 10, label: '10 km' },
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
];

const UI_TEXT = {
  en: {
    title: 'Job Board',
    subtitle: 'Fast, local, mobile-first work discovery.',
    loading: 'Loading jobs...',
    noJobs: 'No jobs available right now',
    noJobsSub: 'Try resetting filters or check back shortly.',
    filters: 'Filters',
    savedJobs: 'Saved jobs',
    savedFilters: 'Saved filters',
    applyNow: 'Apply Now',
    saveFilters: 'Save filters',
    reset: 'Reset',
    clear: 'Clear',
    language: 'Language',
    sort: 'Sort by',
    search: 'Search jobs',
    radius: 'Proximity radius',
    category: 'Industry',
    state: 'State',
    subregion: 'Subregion',
    minRate: 'Minimum rate',
    compliance: 'Compliance',
    tags: 'Tags',
    saveCurrent: 'Save current filters',
    defaultFilters: 'Default filters',
    saved: 'Saved',
    bookmarked: 'Bookmarked',
    unknownRate: 'Negotiable',
    missingCoords: 'No location data',
    proximityOff: 'Proximity unavailable',
    liveMarket: 'Live market',
    availablePostings: 'Available postings',
    highestPay: 'Highest pay',
    jobsByState: 'Jobs by state',
    jobsByCategory: 'Jobs by category',
    savedJobsEmpty: 'No saved jobs yet',
    savedFiltersEmpty: 'No saved filters yet',
    saveFilterName: 'Filter name',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    rename: 'Rename',
  },
  ptBr: {
    title: 'Painel de Vagas',
    subtitle: 'Busca rápida, local e pensada para mobile.',
    loading: 'Carregando vagas...',
    noJobs: 'Nenhuma vaga disponível agora',
    noJobsSub: 'Tente redefinir os filtros ou volte em breve.',
    filters: 'Filtros',
    savedJobs: 'Vagas salvas',
    savedFilters: 'Filtros salvos',
    applyNow: 'Candidatar-se',
    saveFilters: 'Salvar filtros',
    reset: 'Redefinir',
    clear: 'Limpar',
    language: 'Idioma',
    sort: 'Ordenar por',
    search: 'Buscar vagas',
    radius: 'Raio de proximidade',
    category: 'Setor',
    state: 'Estado',
    subregion: 'Sub-região',
    minRate: 'Taxa mínima',
    compliance: 'Conformidade',
    tags: 'Tags',
    saveCurrent: 'Salvar filtros atuais',
    defaultFilters: 'Filtros padrão',
    saved: 'Salvo',
    bookmarked: 'Marcado',
    unknownRate: 'Valor negociável',
    missingCoords: 'Sem dados de localização',
    proximityOff: 'Proximidade indisponível',
    liveMarket: 'Mercado ao vivo',
    availablePostings: 'Vagas disponíveis',
    highestPay: 'Maior pagamento',
    jobsByState: 'Vagas por estado',
    jobsByCategory: 'Vagas por categoria',
    savedJobsEmpty: 'Nenhuma vaga salva ainda',
    savedFiltersEmpty: 'Nenhum filtro salvo ainda',
    saveFilterName: 'Nome do filtro',
    cancel: 'Cancelar',
    save: 'Salvar',
    delete: 'Excluir',
    rename: 'Renomear',
  },
  es: {
    title: 'Panel de Vacantes',
    subtitle: 'Búsqueda rápida, local y optimizada para móvil.',
    loading: 'Cargando vacantes...',
    noJobs: 'No hay vacantes disponibles ahora',
    noJobsSub: 'Prueba restablecer los filtros o vuelve más tarde.',
    filters: 'Filtros',
    savedJobs: 'Vacantes guardadas',
    savedFilters: 'Filtros guardados',
    applyNow: 'Postularse',
    saveFilters: 'Guardar filtros',
    reset: 'Restablecer',
    clear: 'Limpiar',
    language: 'Idioma',
    sort: 'Ordenar por',
    search: 'Buscar vacantes',
    radius: 'Radio de proximidad',
    category: 'Industria',
    state: 'Estado',
    subregion: 'Subregión',
    minRate: 'Tarifa mínima',
    compliance: 'Cumplimiento',
    tags: 'Etiquetas',
    saveCurrent: 'Guardar filtros actuales',
    defaultFilters: 'Filtros predeterminados',
    saved: 'Guardado',
    bookmarked: 'Marcado',
    unknownRate: 'Tarifa negociable',
    missingCoords: 'Sin datos de ubicación',
    proximityOff: 'Proximidad no disponible',
    liveMarket: 'Mercado en vivo',
    availablePostings: 'Vacantes disponibles',
    highestPay: 'Pago más alto',
    jobsByState: 'Vacantes por estado',
    jobsByCategory: 'Vacantes por categoría',
    savedJobsEmpty: 'Aún no hay vacantes guardadas',
    savedFiltersEmpty: 'Aún no hay filtros guardados',
    saveFilterName: 'Nombre del filtro',
    cancel: 'Cancelar',
    save: 'Guardar',
    delete: 'Eliminar',
    rename: 'Renombrar',
  },
  fr: {
    title: 'Tableau des offres',
    subtitle: 'Recherche rapide, locale et pensée pour mobile.',
    loading: 'Chargement des offres...',
    noJobs: 'Aucune offre disponible pour le moment',
    noJobsSub: 'Essayez de réinitialiser les filtres ou revenez plus tard.',
    filters: 'Filtres',
    savedJobs: 'Offres enregistrées',
    savedFilters: 'Filtres enregistrés',
    applyNow: 'Postuler',
    saveFilters: 'Enregistrer les filtres',
    reset: 'Réinitialiser',
    clear: 'Effacer',
    language: 'Langue',
    sort: 'Trier par',
    search: 'Rechercher des offres',
    radius: 'Rayon de proximité',
    category: 'Secteur',
    state: 'État',
    subregion: 'Sous-région',
    minRate: 'Taux minimum',
    compliance: 'Conformité',
    tags: 'Tags',
    saveCurrent: 'Enregistrer les filtres actuels',
    defaultFilters: 'Filtres par défaut',
    saved: 'Enregistré',
    bookmarked: 'Marqué',
    unknownRate: 'Taux négociable',
    missingCoords: 'Aucune donnée de localisation',
    proximityOff: 'Proximité indisponible',
    liveMarket: 'Marché en direct',
    availablePostings: 'Offres disponibles',
    highestPay: 'Meilleur salaire',
    jobsByState: 'Offres par état',
    jobsByCategory: 'Offres par catégorie',
    savedJobsEmpty: 'Aucune offre enregistrée',
    savedFiltersEmpty: 'Aucun filtre enregistré',
    saveFilterName: 'Nom du filtre',
    cancel: 'Annuler',
    save: 'Enregistrer',
    delete: 'Supprimer',
    rename: 'Renommer',
  },
};

const REFERENCE_CODE_PREFIX = /job-[a-z0-9-]+/i;
const STATE_REGEX = /\b(NSW|VIC|QLD|WA)\b/i;
const NUMBER_REGEX = /\$?\s*(\d{2,3}(?:\.\d{1,2})?)/;
const RATE_PATTERNS = [
  /\$\s*(\d{2,3}(?:\.\d{1,2})?)\s*(?:per\s*)?(?:hour|hr|p\/h|ph)\b/i,
  /(\d{2,3}(?:\.\d{1,2})?)\s*(?:per\s*)?(?:hour|hr|p\/h|ph)\b/i,
  /\$\s*(\d{2,3}(?:\.\d{1,2})?)/i,
];

const CATEGORY_KEYWORDS = {
  Construction: ['construction', 'labour', 'labourer', 'builder', 'site', 'trade', 'civil'],
  'Traffic Control': ['traffic', 'controller', 'stop slow', 'road work', 'signage'],
  Warehousing: ['warehouse', 'warehousing', 'picker', 'packer', 'forklift', 'logistics'],
  'Civil Works': ['civil', 'earthworks', 'excavation', 'concrete', 'drainage'],
};

const TRANSLATIONS = {
  en: { whiteCard: 'White Card Required', ppe: 'PPE Required', tools: 'Tools Required', distance: 'km away' },
  ptBr: { whiteCard: 'White Card obrigatório', ppe: 'EPI obrigatório', tools: 'Ferramentas necessárias', distance: 'km de distância' },
  es: { whiteCard: 'White Card requerido', ppe: 'EPP requerido', tools: 'Herramientas requeridas', distance: 'km de distancia' },
  fr: { whiteCard: 'White Card requis', ppe: 'EPI requis', tools: 'Outils requis', distance: 'km de distance' },
};

const getStorage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const loadJson = (key, fallback) => {
  const storage = getStorage();
  if (!storage) return fallback;

  try {
    const raw = storage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const saveJson = (key, value) => {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage write errors
  }
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeText = (value) => String(value || '').toLowerCase();

const detectCategory = (job, text) => {
  const enrichedCategory = job?.enrichedData?.job_category;
  if (enrichedCategory) return enrichedCategory;

  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    if (keywords.some(keyword => text.includes(keyword))) return category;
  }

  return 'Unknown';
};

const detectSubcategory = (job) => {
  const enriched = job?.enrichedData || {};
  return enriched.job_subcategory || job?.referenceCode || 'Job';
};

const detectRate = (job, text) => {
  const salary = job?.enrichedData?.salary || {};
  const directRate = toNumber(salary.rate);
  if (directRate !== null) return { rate: directRate, unit: salary.unit || 'hr' };

  return { rate: null, unit: 'hr' };
};

const detectLocation = (job, text) => {
  const enriched = job?.enrichedData?.location?.[0] || {};
  const fallbackSuburb = job?.suburb || job?.location?.suburb || '';
  const suburbMatch = text.match(/(?:📍|location[:\s-]*|in\s+)([A-Za-z0-9\s.'-]{3,40}?)(?:,\s*(NSW|VIC|QLD|WA)\b|\n|$)/i);
  const stateMatch = text.match(STATE_REGEX);

  const suburb = enriched.suburb || fallbackSuburb || suburbMatch?.[1]?.trim() || '';
  const state = (enriched.state || job?.state || stateMatch?.[1] || '').toUpperCase();
  const subregion = enriched.sub_region || job?.subregion || job?.region || '';
  const coordinates = Array.isArray(enriched.coordinates) && enriched.coordinates.length >= 2
    ? { lat: toNumber(enriched.coordinates[0]), lng: toNumber(enriched.coordinates[1]) }
    : job?.lat && job?.lng
      ? { lat: toNumber(job.lat), lng: toNumber(job.lng) }
      : null;

  return {
    suburb: suburb || '',
    state: state || '',
    subregion: subregion || '',
    coordinates: coordinates?.lat !== null && coordinates?.lng !== null ? coordinates : null,
  };
};

const detectRequirements = (job, text) => {
  const enriched = job?.enrichedData?.requirements || {};
  const whiteCard = enriched.white_card ?? /white\s*card/i.test(text);
  const ppe = enriched.ppe_required ?? /(ppe|boots|hi[-\s]?vis|hard hat)/i.test(text);
  const tools = enriched.own_tools_required ?? /(own\s+tools?|tools|drill|toolbox)/i.test(text);

  return {
    whiteCard: Boolean(whiteCard),
    ppe: Boolean(ppe),
    tools: Boolean(tools),
  };
};

const ALLOWED_TAGS = new Set(['immediate-start', 'abn', 'long-term', 'ongoing', 'tomorrow', 'tomorrow-start', 'high-pay', 'experienced', 'urgent', 'asap', 'overtime-available', 'flexible-rate', 'qualified', 'full-time', 'same-day', 'now', 'travel-paid']);
const detectTags = (job, text, category, requirements) => {
  const enrichedTags = Array.isArray(job?.enrichedData?.tags) ? job.enrichedData.tags : [];
  const tags = new Set();

  enrichedTags.forEach(tag => {
    const t = String(tag).trim().toLowerCase();
    if (ALLOWED_TAGS.has(t)) tags.add(t);
  });

  if (/(immediate start|start asap|urgent|urgent start)/i.test(text)) tags.add('immediate-start');

  let result = Array.from(tags);

  if (result.length === 0) {
    if (/today/i.test(text)) result.push('today');
    else if (/tomorrow/i.test(text)) result.push('tomorrow');
    else if (/next week/i.test(text)) result.push('next week');
  }

  return result.slice(0, 4);
};

const getLanguageDescription = (job, language) => {
  const translated = job?.enrichedData?.translatedDescription?.[language];
  return translated || job?.description || '';
};

const getRawSearchBlob = (job) => {
  const enriched = job?.enrichedData || {};
  const location = enriched.location?.[0] || {};
  const translation = enriched.translatedDescription || {};
  return [
    job?.id,
    job?.referenceCode,
    job?.description,
    enriched.polishedDescription,
    enriched.job_category,
    enriched.job_subcategory,
    enriched.industry,
    enriched.skills?.join(' '),
    enriched.tags?.join(' '),
    location.suburb,
    location.state,
    location.metro_region,
    location.sub_region,
    location.coordinates?.join(' '),
    translation.en,
    translation.ptBr,
    translation.es,
    translation.fr,
  ].filter(Boolean).join(' ');
};

const haversineKm = (a, b) => {
  if (!a || !b) return null;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const aa = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLon * sinLon;
  return 2 * R * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
};

const formatRate = (rate, unit) => {
  if (rate === null || rate === undefined || Number.isNaN(rate)) return null;
  const normalizedUnit = unit || 'hr';
  return `$${Number(rate).toFixed(Number.isInteger(rate) ? 0 : 2)}/${normalizedUnit}`;
};

const loadSavedJobIds = () => {
  const value = loadJson(LOCAL_STORAGE_KEYS.savedJobs, []);
  return Array.isArray(value) ? value.filter(Boolean) : [];
};

function StatCard({ label, value, icon, accent }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: accent || 'primary.50',
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" fontWeight={800} lineHeight={1.1}>{value}</Typography>
      </Box>
    </Paper>
  );
}

function ShareMenu({ job, anchorEl, open, onClose, onToast }) {
  const posthog = usePostHog();
  const jobUrl = `${window.location.origin}/jobs/${job.id}/apply`;
  const jobTitle = job.displayTitle || job.referenceCode || 'Job';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(jobUrl);
      onToast('Job link copied to clipboard');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = jobUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      onToast('Job link copied to clipboard');
    }
    posthog?.capture('job_link_copied', { job_id: job.id });
    onClose();
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: jobTitle, text: job.shareText, url: jobUrl });
        posthog?.capture('job_shared_native', { job_id: job.id, board: 'revamp' });
      } catch (error) {
        if (error?.name !== 'AbortError') {
          await copyToClipboard();
        }
      }
    } else {
      await copyToClipboard();
    }
    onClose();
  };

  const shareWhatsApp = () => {
    const whatsappText = encodeURIComponent(`${job.shareText}\n\n${jobUrl}`);
    window.open(`https://wa.me/?text=${whatsappText}`, '_blank');
    posthog?.capture('job_shared_whatsapp', { job_id: job.id });
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={shareNative}><ListItemText primary="Share" /></MenuItem>
      <MenuItem onClick={copyToClipboard}><ListItemText primary="Copy Link" /></MenuItem>
      <MenuItem onClick={shareWhatsApp}>
        <ListItemIcon><WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} /></ListItemIcon>
        <ListItemText primary="WhatsApp" />
      </MenuItem>
    </Menu>
  );
}

function JobCard({ job, language, saved, onToggleSaved, onApply, onToast }) {
  const theme = useTheme();
  const posthog = usePostHog();
  const [shareAnchorEl, setShareAnchorEl] = useState(null);

  const openShare = Boolean(shareAnchorEl);
  const languageData = TRANSLATIONS[language] || TRANSLATIONS.en;

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 24px rgba(7, 44, 22, 0.08)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'primary.800', color: 'white' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <WorkIcon fontSize="small" />
            <Typography variant="caption" sx={{ letterSpacing: 0.6, textTransform: 'uppercase' }}>
              {job.enrichedData?.job_category || 'Job'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {job.distanceKm !== null && job.distanceKm !== undefined ? (
              <Chip
                size="small"
                icon={<MapIcon />}
                label={`${job.distanceKm.toFixed(1)} ${languageData.distance}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
              />
            ) : (
              <Chip
                size="small"
                label={job.proximityAvailable ? languageData.missingCoords : languageData.proximityOff}
                sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white' }}
              />
            )}
          </Stack>
        </Stack>
      </Box>

      <CardContent sx={{ p: 2, flexGrow: 1 }}>
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                {job.displayTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {job.referenceCode || job.id}
              </Typography>
            </Box>
            <Chip
              size="small"
              icon={<StarIcon fontSize="small" />}
              label={job.rateLabel || languageData.unknownRate}
              color={job.rateValue !== null ? 'secondary' : 'default'}
              sx={{ fontWeight: 700 }}
            />
          </Stack>

          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', lineHeight: 1.55, minHeight: 100, textAlign: 'left' }}
          >
            {job.displayDescription}
          </Typography>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip icon={<LocationIcon />} label={job.locationLabel || 'Location N/A'} size="small" variant="outlined" />
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {job.requirements.whiteCard ? <Chip label={languageData.whiteCard || 'White Card Required'} color="secondary" size="small" /> : null}
            {job.requirements.ppe ? <Chip label={languageData.ppe || 'PPE Required'} color="secondary" size="small" /> : null}
            {job.requirements.tools ? <Chip label={languageData.tools || 'Tools Required'} color="secondary" size="small" /> : null}
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {job.tags.slice(0, 6).map(tag => (
              <Chip key={tag} label={tag} size="small" sx={{ bgcolor: theme.palette.primary[50], color: theme.palette.primary[900] }} />
            ))}
          </Stack>

          <Divider />

          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              {formatJobTimestamp(job.createdAt)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {job.rateLabel || languageData.unknownRate}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={saved ? 'Remove saved job' : 'Save job'}>
            <IconButton onClick={() => onToggleSaved(job.id)} color={saved ? 'primary' : 'default'}>
              {saved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Share">
            <IconButton onClick={(event) => setShareAnchorEl(event.currentTarget)}>
              <ShareIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Button
          variant="contained"
          color="primary"
          onClick={() => onApply(job)}
          endIcon={<OpenInNewIcon />}
          sx={{ borderRadius: 999, px: 2.5, textTransform: 'none', fontWeight: 700, minWidth: 120 }}
        >
          {languageData.applyNow || 'Apply Now'}
        </Button>
      </CardActions>

      <ShareMenu
        job={job}
        anchorEl={shareAnchorEl}
        open={openShare}
        onClose={() => setShareAnchorEl(null)}
        onToast={onToast}
      />
    </Card>
  );
}


function FilterPanel({
  filters,
  onChange,
  language,
  onResetFilters,
  availableCategories,
  availableRoles,
  availableStates,
  availableSubregions,
  onOpenSavedJobs,
  savedJobsCount,
  proximityAvailable,
  onClose,
}) {
  const text = UI_TEXT[language] || UI_TEXT.en;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterIcon color="primary" />
          <Typography variant="h6" fontWeight={800}>{text.filters}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={onOpenSavedJobs}>
            <Badge badgeContent={savedJobsCount} color="secondary">
              <BookmarkIcon />
            </Badge>
          </IconButton>
          {onClose && (
            <Button size="small" variant="contained" color="primary" onClick={onClose}>
              Apply
            </Button>
          )}
        </Stack>
      </Stack>

      <TextField
        label={text.search}
        value={filters.query}
        onChange={(event) => onChange('query', event.target.value)}
        fullWidth
        size="small"
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
      />

      <FormControl fullWidth size="small">
        <InputLabel>{text.radius}</InputLabel>
        <Select value={filters.radiusKm} label={text.radius} onChange={(event) => onChange('radiusKm', event.target.value)}>
          {RADIUS_OPTIONS.map(option => (
            <MenuItem key={String(option.value)} value={option.value}>{option.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>{text.category}</InputLabel>
        <Select value={filters.category} label={text.category} onChange={(event) => onChange('category', event.target.value)}>
          <MenuItem value="all">All Categories</MenuItem>
          {availableCategories.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>Role</InputLabel>
        <Select value={filters.role} label="Role" onChange={(event) => onChange('role', event.target.value)}>
          <MenuItem value="all">All Roles</MenuItem>
          {availableRoles.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>{text.state}</InputLabel>
        <Select value={filters.state} label={text.state} onChange={(event) => onChange('state', event.target.value)}>
          <MenuItem value="all">All States</MenuItem>
          {availableStates.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>{text.subregion}</InputLabel>
        <Select value={filters.subregion} label={text.subregion} onChange={(event) => onChange('subregion', event.target.value)}>
          <MenuItem value="all">All Subregions</MenuItem>
          {availableSubregions.map(option => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={700}>{text.minRate}</Typography>
          <Typography variant="body2" color="text.secondary">${filters.minRate}/hr</Typography>
        </Stack>
        <Slider
          value={filters.minRate}
          onChange={(_, value) => onChange('minRate', value)}
          min={25}
          max={100}
          step={1}
          valueLabelDisplay="auto"
          size="small"
        />
      </Box>

      <FormGroup sx={{ gap: 0 }}>
        <FormControlLabel
          control={<Checkbox size="small" checked={filters.whiteCard} onChange={(event) => onChange('whiteCard', event.target.checked)} />}
          label={<Typography variant="body2">{TRANSLATIONS[language]?.whiteCard || 'White Card Required'}</Typography>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={filters.ppe} onChange={(event) => onChange('ppe', event.target.checked)} />}
          label={<Typography variant="body2">{TRANSLATIONS[language]?.ppe || 'PPE Required'}</Typography>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={filters.tools} onChange={(event) => onChange('tools', event.target.checked)} />}
          label={<Typography variant="body2">{TRANSLATIONS[language]?.tools || 'Tools Required'}</Typography>}
        />
      </FormGroup>

      <Button variant="outlined" color="error" fullWidth onClick={onResetFilters} size="small">
        Remove All Filters
      </Button>

      <Divider />

      <Button variant="outlined" size="small" onClick={onOpenSavedJobs} startIcon={<BookmarkIcon />}>
        {text.savedJobs}
      </Button>
      {!proximityAvailable ? (
        <Alert severity="info" sx={{ py: 0 }}>{UI_TEXT[language]?.proximityOff || 'Proximity unavailable'}</Alert>
      ) : null}
    </Stack>
  );
}


function SavedJobsDrawer({ open, jobs, onClose, onRemove, onClearAll, onApply, language }) {
  const text = UI_TEXT[language] || UI_TEXT.en;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 400 }, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>Saved jobs</Typography>
          <IconButton onClick={onClose}><ClearIcon /></IconButton>
        </Stack>

        {jobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No saved jobs yet</Typography>
        ) : (
          <Stack spacing={1}>
            {jobs.map(job => (
              <Paper key={job.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800}>{job.displayTitle}</Typography>
                    <Typography variant="caption" color="text.secondary">{job.locationLabel || 'Location N/A'}</Typography>
                  </Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="primary.main" fontWeight={700}>{job.rateLabel || text.unknownRate}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Button size="small" onClick={() => onApply(job)} startIcon={<OpenInNewIcon />}>Apply</Button>
                      <IconButton size="small" onClick={() => onRemove(job.id)}><BookmarkIcon color="primary" /></IconButton>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}

        <Button variant="outlined" color="error" fullWidth sx={{ mt: 2 }} onClick={onClearAll}>
          Clear all
        </Button>
      </Box>
    </Drawer>
  );
}




function MapBoundsComponent({ jobs, userLoc }) {
  const map = useMap();
  useEffect(() => {
    if (!jobs || jobs.length === 0) {
      if (userLoc) map.setView([userLoc.lat, userLoc.lng], 13);
      return;
    }
    const bounds = L.latLngBounds(jobs.filter(j => j.coordinates).map(j => [j.coordinates.lat, j.coordinates.lng]));
    if (userLoc) bounds.extend([userLoc.lat, userLoc.lng]);
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, jobs, userLoc]);
  return null;
}

function JobBoardRevamp() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { userTypeData } = useUserType();
  const posthog = usePostHog();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [savedJobs, setSavedJobs] = useState(() => loadSavedJobIds());
  const [language, setLanguage] = useState(() => loadJson(LOCAL_STORAGE_KEYS.savedLanguage, 'en'));
  const [savedJobsOpen, setSavedJobsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '' });
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('idle');

  const jobService = useMemo(() => new JobService(), []);

  useEffect(() => {
    posthog?.capture('job_board_viewed', { board: 'revamp' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    saveJson(LOCAL_STORAGE_KEYS.savedJobs, savedJobs);
  }, [savedJobs]);

  useEffect(() => {
    saveJson(LOCAL_STORAGE_KEYS.savedLanguage, language);
  }, [language]);

  useEffect(() => {
    const fetchJobs = async () => {
      const cacheKey = 'job-board-revamp:jobs';
      const cached = jobsCache.get(cacheKey);
      if (cached) {
        setJobs(cached);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const result = await jobService.getAllJobs({ limit: 100 });
        const items = result.items || [];
        jobsCache.set(cacheKey, items);
        setJobs(items);
      } catch (fetchError) {
        setError('Failed to load jobs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [jobService]);

  useEffect(() => {
    let cancelled = false;
    const profile = userTypeData?.profiles?.jobSeeker;

    const profileLat = toNumber(profile?.lat);
    const profileLng = toNumber(profile?.lng);

    if (profileLat !== null && profileLng !== null) {
      setUserLocation({ lat: profileLat, lng: profileLng, source: 'profile' });
      setLocationStatus('profile');
      return () => { cancelled = true; };
    }

    if (!navigator?.geolocation) {
      setLocationStatus('unavailable');
      return () => { cancelled = true; };
    }

    setLocationStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) return;
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          source: 'browser',
        });
        setLocationStatus('browser');
      },
      () => {
        if (cancelled) return;
        setUserLocation(null);
        setLocationStatus('denied');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );

    return () => { cancelled = true; };
  }, [userTypeData?.profiles?.jobSeeker]);

  const normalizedJobs = useMemo(() => {
    return jobs.map(job => {
      const description = String(job?.description || '');
      const searchBlob = normalizeText(getRawSearchBlob(job));
      const displayDescription = getLanguageDescription(job, language);
      const displayTitle = detectSubcategory(job) || job.referenceCode || `Job ${job.id}`;
      const category = detectCategory(job, searchBlob);
      const { rate, unit } = detectRate(job, searchBlob);
      const rateLabel = formatRate(rate, unit);
      const location = detectLocation(job, searchBlob);
      const requirements = detectRequirements(job, searchBlob);
      const tags = detectTags(job, searchBlob, category, requirements);
      const allText = normalizeText([displayTitle, displayDescription, job.referenceCode, category, location.suburb, location.state, location.subregion, tags.join(' ')].join(' '));
      const coords = location.coordinates;

      return {
        ...job,
        displayTitle,
        displayDescription,
        category,
        rateValue: rate,
        rateLabel,
        locationLabel: [location.suburb, location.subregion, location.state].filter(Boolean).join(', '),
        suburb: location.suburb,
        state: location.state,
        subregion: location.subregion,
        coordinates: coords,
        requirements,
        tags,
        searchBlob: allText,
        proximityAvailable: Boolean(userLocation && coords),
        distanceKm: userLocation && coords ? haversineKm(userLocation, coords) : null,
        shareText: `Check out this ${displayTitle} position${job.company ? ` at ${job.company}` : ''}`,
      };
    });
  }, [jobs, language, userLocation]);

  const { availableCategories, availableRoles, availableStates, availableSubregions } = useMemo(() => {
    const cats = new Set();
    const roles = new Set();
    const states = new Set();
    const subregs = new Set();
    normalizedJobs.forEach(job => {
      if (job.enrichedData?.job_category) cats.add(job.enrichedData.job_category);
      if (job.enrichedData?.job_subcategory) roles.add(job.enrichedData.job_subcategory);
      if (job.state) states.add(job.state);
      if (job.subregion) subregs.add(job.subregion);
    });
    return {
      availableCategories: Array.from(cats).sort((a, b) => a.localeCompare(b)),
      availableRoles: Array.from(roles).sort((a, b) => a.localeCompare(b)),
      availableStates: Array.from(states).sort((a, b) => a.localeCompare(b)),
      availableSubregions: Array.from(subregs).sort((a, b) => a.localeCompare(b))
    };
  }, [normalizedJobs]);

  const marketSummary = useMemo(() => {
    const total = normalizedJobs.length;
    const highestPay = normalizedJobs.reduce((max, job) => Math.max(max, job.rateValue || 0), 0);

    const bySubregion = normalizedJobs.reduce((acc, job) => {
      if (!job.subregion) return acc;
      acc[job.subregion] = (acc[job.subregion] || 0) + 1;
      return acc;
    }, {});
    const topAreas = Object.entries(bySubregion).sort((a, b) => b[1] - a[1]).slice(0, 3);

    const bySubCategory = normalizedJobs.reduce((acc, job) => {
      const key = job.enrichedData?.job_subcategory || 'Unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const topCategories = Object.entries(bySubCategory).sort((a, b) => b[1] - a[1]).slice(0, 3);

    return {
      total,
      highestPay,
      topAreas,
      topCategories,
    };
  }, [normalizedJobs]);

  const filteredJobs = useMemo(() => {
    const query = normalizeText(filters.query);
    // const tagQuery = normalizeText(filters.tags);

    const withinRadius = (job) => {
      if (filters.radiusKm === 'all') return true;
      if (!userLocation || job.distanceKm === null || job.distanceKm === undefined) return true;
      return job.distanceKm <= Number(filters.radiusKm);
    };

    const matchesFilter = (job) => {
      if (query && !job.searchBlob.includes(query)) return false;
      if (filters.category !== 'all' && job.enrichedData?.job_category !== filters.category) return false;
      const jobRole = job.enrichedData?.job_subcategory || 'Unknown';
      if (filters.role !== 'all' && jobRole !== filters.role) return false;
      if (filters.state !== 'all' && job.state !== filters.state) return false;
      if (filters.subregion !== 'all' && job.subregion !== filters.subregion) return false;
      if (filters.minRate && job.rateValue !== null && job.rateValue < filters.minRate) return false;

      if (filters.whiteCard && !job.requirements.whiteCard) return false;
      if (filters.ppe && !job.requirements.ppe) return false;
      if (filters.tools && !job.requirements.tools) return false;



      if (!withinRadius(job)) return false;

      return true;
    };

    const result = normalizedJobs.filter(matchesFilter);

    return result.sort((left, right) => {
      if (filters.sortBy === 'payRate') {
        const leftRate = left.rateValue ?? -1;
        const rightRate = right.rateValue ?? -1;
        return rightRate - leftRate || new Date(right.createdAt || 0) - new Date(left.createdAt || 0);
      }

      if (filters.sortBy === 'distance') {
        const leftDistance = left.distanceKm ?? Number.POSITIVE_INFINITY;
        const rightDistance = right.distanceKm ?? Number.POSITIVE_INFINITY;
        return leftDistance - rightDistance || new Date(right.createdAt || 0) - new Date(left.createdAt || 0);
      }

      return new Date(right.createdAt || 0) - new Date(left.createdAt || 0);
    });
  }, [filters, normalizedJobs, userLocation]);

  const savedJobCards = useMemo(() => {
    const savedSet = new Set(savedJobs);
    return normalizedJobs.filter(job => savedSet.has(job.id));
  }, [normalizedJobs, savedJobs]);

  const savedJobsCount = savedJobCards.length;
  const text = UI_TEXT[language] || UI_TEXT.en;

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    posthog?.capture('job_board_filter_applied', { filter_key: key, filter_value: value, board: 'revamp' });
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    posthog?.capture('job_board_filters_reset', { board: 'revamp' });
  };

  const toggleSavedJob = (jobId) => {
    setSavedJobs((current) => {
      if (current.includes(jobId)) {
        return current.filter(id => id !== jobId);
      }
      posthog?.capture('job_saved', { job_id: jobId, board: 'revamp' });
      return [...current, jobId];
    });
  };

  const clearSavedJobs = () => setSavedJobs([]);

  const onApply = (job) => {
    posthog?.capture('job_card_clicked', {
      job_id: job.id,
      job_reference: job.referenceCode,
      job_category: job.enrichedData?.job_category,
      card_type: 'revamp',
    });
    navigate(`/jobs/${job.id}/apply`);
  };

  const switchToOldBoard = () => {
    posthog?.capture('job_board_switched', { from: 'revamp', to: 'classic' });
    navigate('/jobs-classic');
  };

  const renderHeaderActions = () => (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
      <IconButton color="inherit" onClick={(e) => setLanguageAnchorEl(e.currentTarget)}>
        <LanguageIcon />
      </IconButton>
      <Menu
        anchorEl={languageAnchorEl}
        open={Boolean(languageAnchorEl)}
        onClose={() => setLanguageAnchorEl(null)}
      >
        {LANGUAGES.map(option => (
          <MenuItem
            key={option.value}
            selected={option.value === language}
            onClick={() => {
              setLanguage(option.value);
              setLanguageAnchorEl(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      <Button variant="outlined" startIcon={<BookmarkIcon />} onClick={() => setSavedJobsOpen(true)}>
        {text.savedJobs}
      </Button>
      <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => setFiltersOpen(true)} sx={{ display: { md: 'none' } }}>
        {text.filters}
      </Button>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={resetFilters}>
        {text.reset}
      </Button>
    </Stack>
  )

  const drawerContent = (
    <FilterPanel
      filters={filters}
      onChange={updateFilter}
      language={language}
      onResetFilters={resetFilters}
      availableCategories={availableCategories}
      availableRoles={availableRoles}
      availableStates={availableStates}
      availableSubregions={availableSubregions}
      onOpenSavedJobs={() => setSavedJobsOpen(true)}
      savedJobsCount={savedJobsCount}
      proximityAvailable={Boolean(userLocation)}
    />
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'primary.800' }}>
          <Toolbar>
            <Typography variant="h6" fontWeight={800}>{text.title}</Typography>
          </Toolbar>
        </AppBar>
        <Container sx={{ py: 4 }}>
          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <Stack spacing={2} alignItems="center">
              <CircularProgress />
              <Typography variant="h6">{text.loading}</Typography>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'primary.800', backgroundImage: (t) => `linear-gradient(90deg, ${t.palette.primary[800]} 0%, ${t.palette.primary.main} 100%)` }}>
        <Toolbar sx={{ flexWrap: 'wrap', gap: 1, py: 0.5, minHeight: 48 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.15)', display: 'grid', placeItems: 'center' }}>
              <WorkIcon fontSize="small" />
            </Box>
            <Typography variant="subtitle1" fontWeight={900} noWrap>{text.title}</Typography>
          </Stack>
          {renderHeaderActions()}
        </Toolbar>
      </AppBar> */}

      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, pt: 2.5, pb: { xs: 10, md: 3 } }}>
        <Container maxWidth="xl" disableGutters>
          <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, bgcolor: 'primary.800', color: 'white', backgroundImage: (t) => `linear-gradient(90deg, ${t.palette.primary[800]} 0%, ${t.palette.primary.main} 100%)` }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.15)', display: 'grid', placeItems: 'center' }}>
                  <WorkIcon />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={900}>{text.title}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>{text.subtitle}</Typography>
                </Box>
              </Stack>
              {renderHeaderActions()}
            </Stack>
          </Paper>
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 3 }} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Paper sx={{ position: 'sticky', top: 88, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                {drawerContent}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 9 }}>
              <Stack spacing={2.5}>
                {isDesktop ? (
                  <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} sx={{ display: 'flex', overflowX: 'auto', pb: 1 }}>
                        <Box sx={{ flexShrink: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Top Areas</Typography>
                          <Stack direction="row" spacing={1}>
                            {marketSummary.topAreas.map(([area, count]) => (
                              <Chip
                                key={area}
                                label={`${area} (${count})`}
                                size="small"
                                icon={<LocationIcon />}
                                onClick={() => updateFilter('subregion', filters.subregion === area ? 'all' : area)}
                                color={filters.subregion === area ? 'primary' : 'default'}
                                variant={filters.subregion === area ? 'filled' : 'outlined'}
                              />
                            ))}
                          </Stack>
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Top Roles</Typography>
                          <Stack direction="row" spacing={1}>
                            {marketSummary.topCategories.map(([cat, count]) => (
                              <Chip
                                key={cat}
                                label={`${cat} (${count})`}
                                size="small"
                                icon={<WorkIcon />}
                                onClick={() => updateFilter('role', filters.role === cat ? 'all' : cat)}
                                color={filters.role === cat ? 'primary' : 'default'}
                                variant={filters.role === cat ? 'filled' : 'outlined'}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </Stack>
                    </Stack>
                  </Paper>
                ) : (
                  <Accordion disableGutters elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2" fontWeight={800}>Market Insights & Stats</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Top Areas</Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {marketSummary.topAreas.map(([area, count]) => (
                              <Chip
                                key={area}
                                label={`${area} (${count})`}
                                size="small"
                                icon={<LocationIcon />}
                                onClick={() => updateFilter('subregion', filters.subregion === area ? 'all' : area)}
                                color={filters.subregion === area ? 'primary' : 'default'}
                                variant={filters.subregion === area ? 'filled' : 'outlined'}
                              />
                            ))}
                          </Stack>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Top Roles</Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {marketSummary.topCategories.map(([cat, count]) => (
                              <Chip
                                key={cat}
                                label={`${cat} (${count})`}
                                size="small"
                                icon={<WorkIcon />}
                                onClick={() => updateFilter('role', filters.role === cat ? 'all' : cat)}
                                color={filters.role === cat ? 'primary' : 'default'}
                                variant={filters.role === cat ? 'filled' : 'outlined'}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                {error ? <Alert severity="error">{error}</Alert> : null}

                {filtersOpen && !isDesktop ? (
                  <Drawer anchor="bottom" open={filtersOpen} onClose={() => setFiltersOpen(false)}>
                    <Box sx={{ maxHeight: '85vh', overflow: 'auto' }}>
                      <FilterPanel
                        filters={filters}
                        onChange={updateFilter}
                        language={language}
                        onResetFilters={resetFilters}
                        availableCategories={availableCategories}
                        availableRoles={availableRoles}
                        availableStates={availableStates}
                        availableSubregions={availableSubregions}
                        onOpenSavedJobs={() => setSavedJobsOpen(true)}
                        savedJobsCount={savedJobsCount}
                        proximityAvailable={Boolean(userLocation)}
                        onClose={() => setFiltersOpen(false)}
                      />
                    </Box>
                  </Drawer>
                ) : null}

                {savedJobsOpen ? (
                  <SavedJobsDrawer
                    open={savedJobsOpen}
                    jobs={savedJobCards}
                    language={language}
                    onClose={() => setSavedJobsOpen(false)}
                    onRemove={toggleSavedJob}
                    onClearAll={clearSavedJobs}
                    onApply={onApply}
                  />
                ) : null}

                <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Typography variant="h6" fontWeight={800}>
                        {filteredJobs.length} results
                      </Typography>
                      {savedJobsCount > 0 && <Chip label={`Saved: ${savedJobsCount}`} size="small" color="primary" />}
                    </Stack>

                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      <TextField
                        select
                        size="small"
                        label={text.sort}
                        value={filters.sortBy}
                        onChange={(event) => updateFilter('sortBy', event.target.value)}
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="createdAt">Published Date</MenuItem>
                        <MenuItem value="payRate">Highest Pay</MenuItem>
                        <MenuItem value="distance">Closest Location</MenuItem>
                      </TextField>

                      <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_, newMode) => { if (newMode) setViewMode(newMode); }}
                        size="small"
                      >
                        <ToggleButton value="list">
                          <ListIcon sx={{ mr: 0.5 }} fontSize="small" /> List
                        </ToggleButton>
                        <ToggleButton value="map">
                          <MapIcon sx={{ mr: 0.5 }} fontSize="small" /> Map
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>
                  </Stack>
                </Paper>

                {filteredJobs.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                    <WorkIcon sx={{ fontSize: 72, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="h5" fontWeight={800}>{text.noJobs}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{text.noJobsSub}</Typography>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={resetFilters}>{text.reset}</Button>
                  </Paper>
                ) : (
                  viewMode === 'list' ? (
                    <Grid container spacing={2}>
                      {filteredJobs.map(job => (
                        <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={job.id}>
                          <JobCard
                            job={job}
                            language={language}
                            saved={savedJobs.includes(job.id)}
                            onToggleSaved={toggleSavedJob}
                            onApply={onApply}
                            onToast={(message) => setToast({ open: true, message })}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Paper sx={{ height: '70vh', minHeight: 500, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                      <MapContainer center={[-25.2744, 133.7751]} zoom={4} style={{ height: '100%', width: '100%' }}>
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <MapBoundsComponent jobs={filteredJobs} userLoc={userLocation?.lat ? userLocation : null} />
                        {userLocation?.lat && (
                          <Marker position={[userLocation.lat, userLocation.lng]} icon={UserIcon}>
                            <Popup><strong>You are here</strong></Popup>
                          </Marker>
                        )}
                        {filteredJobs.filter(j => j.coordinates).map(job => (
                          <Marker key={job.id} position={[job.coordinates.lat, job.coordinates.lng]} icon={JobIcon}>
                            <Popup>
                              <Box sx={{ minWidth: 200, py: 0.5 }}>
                                <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                                  {job.displayTitle}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                  {job.locationLabel}
                                </Typography>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                                  <Typography variant="body2" color="primary" fontWeight={700}>
                                    {job.rateLabel || text.unknownRate}
                                  </Typography>
                                  <Button size="small" variant="contained" onClick={() => onApply(job)}>Apply</Button>
                                </Stack>
                              </Box>
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    </Paper>
                  )
                )}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Paper
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          left: 12,
          right: 12,
          bottom: 12,
          zIndex: theme.zIndex.appBar + 1,
          borderRadius: 999,
          p: 1,
          boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" startIcon={<FilterIcon />} onClick={() => setFiltersOpen(true)}>
            {text.filters}
          </Button>
          <Button fullWidth variant="outlined" startIcon={<BookmarkIcon />} onClick={() => setSavedJobsOpen(true)}>
            {text.savedJobs}
          </Button>
        </Stack>
      </Paper>



      <Snackbar
        open={toast.open}
        autoHideDuration={2800}
        onClose={() => setToast({ open: false, message: '' })}
        message={toast.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default JobBoardRevamp;
